import React, { FC } from 'react';
import { Alert, Button, Card, ConfigProvider, Spin } from 'antd';
import { ArrowLeftOutlined, FullscreenOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { fetchWalkthroughsForHeatmap } from '../../thunks/walkthroughs_thunks';
import { withUserType } from '../withUserType';
import { getWalkthroughHeatMapRangesForSiteSurvey } from '../../utils/WalkthroughHeatMapRangeUtils';
import { WalkthroughHeatMapRange } from '../../types/walkthrough_heat_map_ranges';
import WalkthroughHeatMapLegend from '../walkthrough_heat_map_ranges/WalkthroughHeatMapLegend';
import { WalkthroughHeatmapCalendar } from './WalkthroughHeatmapCalendar';
import { useWalkthroughHeatmapData } from './useWalkthroughHeatmapData';
import { FetchHeatmapWalkthroughs, HeatmapMonth } from './heatmapUtils';
import { FIXED_CELL_PX } from './heatmapLayout';
import useWindowWidth from '../../hooks/useWindowWidth';

// Fetched once and shared: the card shows as many of these as fit its width, ending with the
// current month, and the full-screen view shows them all.
const MONTH_COUNT = 12;
// The full-screen view: up to two rows of six months, with cells 1.1x the card's (whole pixels) and
// a fixed 20px between months (a 12px spacer plus the grid's two 4px gaps). On a narrower window the
// months wrap to 4, 3, 2 or 1 per row instead.
const EXPANDED_MONTHS_PER_ROW = 6;
const EXPANDED_CELL_PX = Math.round(FIXED_CELL_PX * 1.1);
const EXPANDED_MONTH_SPACER_PX = 12;
// Room the calendar can't use across the window: the view's 24px (px-l) side padding and 16px for
// its scrollbar.
const EXPANDED_HORIZONTAL_ROOM_PX = 2 * 24 + 16;

// The heatmap's take-latest slot (registered in walkthroughs_reducer).
const TARGET_COLLECTION = 'walkthroughHeatmap';

interface HeatmapPanelProps {
	months: HeatmapMonth[];
	loading: boolean;
	error: string | null;
	timeZoneId?: string;
	userType: string;
	heatMapRanges: WalkthroughHeatMapRange[];
}

interface HeatmapPanelLayoutProps extends HeatmapPanelProps {
	monthsPerRow: number;
	fitMonthsToWidth?: boolean;
	cellPx?: number;
	monthSpacerPx?: number;
	wrapToWidthPx?: number;
	// Leave the legend out, for a view that shows it elsewhere (the full-screen view, in its header).
	hideLegend?: boolean;
}

// One view of the months the card fetched: the card's own, or the full-screen one.
const HeatmapPanel: FC<HeatmapPanelLayoutProps> = ({
	months,
	loading,
	error,
	timeZoneId,
	userType,
	heatMapRanges,
	monthsPerRow,
	fitMonthsToWidth,
	cellPx,
	monthSpacerPx,
	wrapToWidthPx,
	hideLegend,
}) => {
	if (error) {
		return (
			<Alert
				type="error"
				showIcon
				message="Couldn't load walkthrough scores."
				description={error}
			/>
		);
	}

	return (
		<Spin spinning={loading}>
			<WalkthroughHeatmapCalendar
				months={months}
				monthsPerRow={monthsPerRow}
				fitMonthsToWidth={fitMonthsToWidth}
				cellPx={cellPx}
				monthSpacerPx={monthSpacerPx}
				wrapToWidthPx={wrapToWidthPx}
				heatMapRanges={heatMapRanges}
				userType={userType}
				timeZoneId={timeZoneId}
			/>
			{/* ml-11 lines the first swatch up with the first week: weekday column (w-8) + 12px to the first month */}
			{hideLegend ? null : (
				<WalkthroughHeatMapLegend ranges={heatMapRanges} className="ml-11 mt-4" />
			)}
		</Spin>
	);
};

interface WalkthroughHeatmapProps {
	getWalkthroughData: FetchHeatmapWalkthroughs;
	siteSurveyId: string;
	userType: string;
	timeZoneId?: string;
	siteSurvey: { walkthroughHeatMapRanges?: WalkthroughHeatMapRange[] | null } | null;
}

const WalkthroughHeatmap: FC<WalkthroughHeatmapProps> = ({
	getWalkthroughData,
	siteSurveyId,
	userType,
	timeZoneId,
	siteSurvey,
}) => {
	const [expandedViewVisible, setExpandedViewVisible] = React.useState(false);
	const expandedViewRef = React.useRef<HTMLDivElement>(null);

	// The survey's ranges, already resolved against its template by the backend, else the defaults.
	const heatMapRanges = React.useMemo(
		() => getWalkthroughHeatMapRangesForSiteSurvey(siteSurvey),
		[siteSurvey]
	);

	const { loading, error, months } = useWalkthroughHeatmapData(
		getWalkthroughData,
		siteSurveyId,
		timeZoneId,
		MONTH_COUNT,
		TARGET_COLLECTION
	);

	const windowWidth = useWindowWidth();

	const openExpandedView = () => setExpandedViewVisible(true);
	const closeExpandedView = () => setExpandedViewVisible(false);
	// Keeps the day popovers inside the full-screen view, as LocationsMapView does: portalled to the
	// body they would sit in a different stacking context from the fixed overlay.
	const getExpandedViewPopupContainer = () => expandedViewRef.current || document.body;

	const panelProps: HeatmapPanelProps = {
		months,
		loading,
		error,
		timeZoneId,
		userType,
		heatMapRanges,
	};

	return (
		<Card
			title="Walkthrough scores"
			extra={
				<Button type="link" icon={<FullscreenOutlined />} onClick={openExpandedView}>
					View last {MONTH_COUNT} months
				</Button>
			}
			style={{ height: '100%' }}
		>
			<HeatmapPanel {...panelProps} monthsPerRow={MONTH_COUNT} fitMonthsToWidth />
			{expandedViewVisible ? (
				// Full screen over the page, like the locations map view: a header with a way back and
				// the legend, then the months, which scroll when they're taller than the window.
				<div
					ref={expandedViewRef}
					className="fixed inset-0 z-[1000] flex flex-col overflow-hidden bg-white"
				>
					<ConfigProvider getPopupContainer={getExpandedViewPopupContainer}>
						<div className="flex shrink-0 items-center gap-m px-l py-m">
							<Button type="default" icon={<ArrowLeftOutlined />} onClick={closeExpandedView}>
								Back
							</Button>
							<div className="h3regular mb-0 text-character-primary">
								Walkthrough scores — last {MONTH_COUNT} months
							</div>
							{/* In the header, which never scrolls, so it stays in view over every month. */}
							<WalkthroughHeatMapLegend ranges={heatMapRanges} className="ml-auto" />
						</div>
						<div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-l">
							{/* w-fit + m-auto centers the calendar both ways in the room under the header. Auto
							margins fall to 0 when the months are taller than the room, so unlike justify-center
							they never push the first row up out of reach of the scroll. */}
							<div className="m-auto w-fit">
								<HeatmapPanel
									{...panelProps}
									monthsPerRow={EXPANDED_MONTHS_PER_ROW}
									cellPx={EXPANDED_CELL_PX}
									monthSpacerPx={EXPANDED_MONTH_SPACER_PX}
									wrapToWidthPx={windowWidth - EXPANDED_HORIZONTAL_ROOM_PX}
									hideLegend
								/>
							</div>
						</div>
					</ConfigProvider>
				</div>
			) : null}
		</Card>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	getWalkthroughData: (siteSurveyId, startDate, endDate, targetCollectionName) =>
		dispatch(
			fetchWalkthroughsForHeatmap(ownProps.userType)(
				siteSurveyId,
				startDate,
				endDate,
				targetCollectionName
			)
		),
});

export default withUserType(connect(null, mapDispatchToProps)(WalkthroughHeatmap));
